"use client";

import { useState, useEffect } from "react";
import {
    Button,
    Table,
    Tag as AntTag,
    Drawer,
    Form,
    Input,
    Spin,
    message,
    Card,
    Space,
    Popconfirm,
    Switch,
    Badge,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import useTagsStore, { Tag } from "./TagsStore.ts";

const { TextArea } = Input;

export default function Tags() {
    const {
        getTags,
        getTagById,
        createTag,
        updateTag,
        deleteTag,
        tagsData,
        tagData,
        isListLoading,
        isDetailLoading,
        isSaving,
    } = useTagsStore();

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(30);
    const [searchText, setSearchText] = useState("");
    const [form] = Form.useForm();

    useEffect(() => {
        fetchTags();
    }, [currentPage, pageSize, searchText]);

    const fetchTags = () => {
        const params = {
            page: currentPage,
            per_page: pageSize,
            search: searchText || undefined,
        };
        getTags(params);
    };

    const handleTableChange = (pagination: TablePaginationConfig) => {
        setCurrentPage(pagination.current || 1);
        setPageSize(pagination.pageSize || 30);
    };

    const handleSearch = (value: string) => {
        setSearchText(value);
        setCurrentPage(1);
    };

    const handleAddTag = () => {
        setIsEditMode(false);
        form.resetFields();
        setIsDrawerOpen(true);
    };

    const handleEditTag = async (id: number) => {
        setIsEditMode(true);
        setIsDrawerOpen(true);
        form.resetFields();
        const result = await getTagById(id);
        if (result.success && result.data) {
            form.setFieldsValue(result.data.data);
        } else {
            message.error("Failed to load tag details");
        }
    };

    const handleDeleteTag = async (id: number) => {
        const result = await deleteTag(id);
        if (result.success) {
            fetchTags();
        }
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            let result;
            if (isEditMode && tagData?.data?.id) {
                result = await updateTag(tagData.data.id, values);
            } else {
                result = await createTag(values);
            }

            if (result.success) {
                setIsDrawerOpen(false);
                fetchTags();
            }
        } catch (err) {
            console.error(err);
        }
    };

    const columns: ColumnsType<Tag> = [
        {
            title: (
                <span className="flex items-center gap-2">
                    <i className="fa-solid fa-tag text-black"></i>
                    <span className="font-semibold">Tag Name</span>
                </span>
            ),
            key: "tag_info",
            render: (_, record) => (
                <div className="flex items-center gap-3">
                    <div
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{
                            backgroundColor: record.color || "#FEF3C7",
                            border: "2px solid #FFCB37"
                        }}
                    >
                        {record.icon ? (
                            <i className={`${record.icon} text-lg`} style={{ color: "#000" }}></i>
                        ) : (
                            <i className="fa-solid fa-tag text-primary-600"></i>
                        )}
                    </div>
                    <div>
                        <div className="font-semibold text-gray-900">{record.display_name}</div>
                        <div className="text-xs text-gray-500">#{record.name}</div>
                    </div>
                </div>
            ),
        },
        {
            title: (
                <span className="flex items-center gap-2">
                    <i className="fa-solid fa-align-left text-black"></i>
                    <span className="font-semibold">Description</span>
                </span>
            ),
            dataIndex: "description",
            key: "description",
            render: (text) => (
                <span className="text-gray-600">{text || "—"}</span>
            ),
        },
        {
            title: (
                <span className="flex items-center gap-2">
                    <i className="fa-solid fa-chart-line text-black"></i>
                    <span className="font-semibold">Usage</span>
                </span>
            ),
            dataIndex: "usage_count",
            key: "usage_count",
            align: "center",
            render: (count) => (
                <Badge
                    count={count || 0}
                    showZero
                    style={{ backgroundColor: "#FFCB37", color: "#000" }}
                />
            ),
        },
        {
            title: (
                <span className="flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-black"></i>
                    <span className="font-semibold">Status</span>
                </span>
            ),
            dataIndex: "is_active",
            key: "is_active",
            render: (is_active: boolean) => (
                <AntTag
                    icon={
                        is_active ?
                            <i className="fa-solid fa-circle-check mr-1"></i> :
                            <i className="fa-solid fa-ban mr-1"></i>
                    }
                    color={is_active ? "success" : "error"}
                >
                    {is_active ? "Active" : "Inactive"}
                </AntTag>
            ),
        },
        {
            title: <span className="font-semibold">Actions</span>,
            key: "action",
            align: "center",
            width: 160,
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<i className="fa-solid fa-pen-to-square"></i>}
                        type="text"
                        className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => handleEditTag(record.id)}
                    />
                    <Popconfirm
                        title="Delete this tag?"
                        description="Are you sure to delete this tag?"
                        onConfirm={() => handleDeleteTag(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            icon={<i className="fa-solid fa-trash"></i>}
                            type="text"
                            danger
                            className="hover:bg-red-50"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const paginationConfig = {
        current: tagsData?.current_page || 1,
        pageSize: tagsData?.per_page || 30,
        total: tagsData?.total || 0,
        showSizeChanger: true,
        showTotal: (total: number, range: [number, number]) =>
            `${range[0]}-${range[1]} of ${total} tags`,
    };

    return (
        <>
            <div className="mb-8">
                <div className="flex items-center gap-2 text-sm text-gray-400 mb-2 font-medium">
                    <span>Home</span>
                    <span>/</span>
                    <span className="text-black">Tags</span>
                </div>
                <h1 className="text-3xl font-bold text-black">Tags Management</h1>
            </div>

            <div className="bg-white rounded-3xl shadow-soft border border-gray-100 overflow-hidden">
                <div className="p-6">
                    <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center text-primary-600">
                                <i className="fa-solid fa-tags text-black text-lg"></i>
                            </div>
                            <h3 className="text-lg font-bold text-black m-0">All Tags</h3>
                        </div>
                        <div className="flex items-center gap-3">
                            <Input.Search
                                placeholder="Search tags..."
                                onSearch={handleSearch}
                                style={{ width: 300 }}
                                className="rounded-xl"
                                prefix={<i className="fa-solid fa-magnifying-glass text-gray-400"></i>}
                            />
                            <Button
                                type="primary"
                                icon={<i className="fa-solid fa-plus text-black"></i>}
                                className="shadow-lg shadow-yellow-200/50"
                                onClick={handleAddTag}
                            >
                                Add New Tag
                            </Button>
                        </div>
                    </div>
                    <Table
                        columns={columns}
                        dataSource={tagsData?.data}
                        loading={isListLoading}
                        rowKey="id"
                        pagination={paginationConfig}
                        onChange={handleTableChange}
                    />
                </div>
            </div>

            <Drawer
                title={
                    <span className="flex items-center gap-2 text-lg">
                        <i className="fa-solid fa-tag text-black"></i>
                        {isEditMode ? "Edit Tag" : "New Tag"}
                    </span>
                }
                open={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                width={520}
                destroyOnClose
                footer={
                    <div className="flex justify-end gap-2">
                        <Button onClick={() => setIsDrawerOpen(false)}>Cancel</Button>
                        <Button type="primary" onClick={handleSave} loading={isSaving}>
                            Save
                        </Button>
                    </div>
                }
            >
                {isDetailLoading ? (
                    <div className="flex justify-center items-center h-40">
                        <Spin tip="Loading..." />
                    </div>
                ) : (
                    <Form form={form} layout="vertical" className="pt-4">
                        <Form.Item
                            name="name"
                            label="Tag Name"
                            rules={[{ required: true, message: "Please enter tag name" }]}
                            extra="Lowercase, no spaces (e.g., romantic, coffee, wifi)"
                        >
                            <Input placeholder="e.g., romantic" />
                        </Form.Item>

                        <Form.Item
                            name="display"
                            label="Display Tag"
                            valuePropName="checked"
                            extra="Show this tag to users"
                        >
                            <Switch defaultChecked />
                        </Form.Item>
                    </Form>
                )}
            </Drawer>
        </>
    );
}
